import type { ReasoningEffortOverride } from "@/stores/ai-explorer-store"

// ---------------------------------------------------------------------------
// Auto mode heuristic: classify query complexity → effort level
// ---------------------------------------------------------------------------

// Complexity trigger phrases per locale. Matching any phrase → high effort.
const COMPLEXITY_PHRASES: Record<string, string[]> = {
  en: [
    "step by step",
    "analyze",
    "analyse",
    "compare",
    "explain why",
    "pros and cons",
    "evaluate",
    "debug",
    "what's wrong",
    "what is wrong",
    "trade-off",
    "tradeoff",
    "architecture",
    "design",
  ],
  sv: [
    "steg för steg",
    "analysera",
    "jämför",
    "förklara varför",
    "fördelar och nackdelar",
    "utvärdera",
    "felsök",
    "vad är fel",
    "avvägning",
  ],
  no: [
    "steg for steg",
    "analyser",
    "analysere",
    "sammenlign",
    "forklar hvorfor",
    "fordeler og ulemper",
    "evaluer",
    "vurder",
    "feilsøk",
    "hva er galt",
    "avveining",
  ],
  fi: [
    "askel askeleelta",
    "analysoi",
    "vertaa",
    "selitä miksi",
    "hyödyt ja haitat",
    "arvioi",
    "debuggaa",
    "mikä on vialla",
    "kompromissi",
  ],
  de: [
    "schritt für schritt",
    "analysiere",
    "analysieren",
    "vergleiche",
    "vergleichen",
    "erkläre warum",
    "vor- und nachteile",
    "bewerte",
    "bewerten",
    "debuggen",
    "was ist falsch",
    "abwägung",
  ],
}

/**
 * Classify a question's complexity to pick the right reasoning effort for Auto mode.
 * Pure function, zero latency cost.
 */
export function classifyAutoEffort(
  question: string,
  fileCount: number,
  locale: string = "en"
): ReasoningEffortOverride {
  // Auto mode biases toward FAST: gpt-5.x reasons adaptively even at low
  // effort, so most questions get a good answer without paying for higher
  // effort. The previous heuristic escalated almost everything (>=80 chars ->
  // medium; common words like "analyze"/"compare" -> high), pushing ordinary
  // questions onto "Thinking" far more often than the question warranted.
  //
  // New policy: Auto NEVER escalates to HIGH — that tier is reserved for the
  // explicit /bigbrain opt-in. A strong, unambiguous signal of a genuinely
  // hard task (pasted code, attached files, or an explicit complexity phrase)
  // nudges to MEDIUM only; everything else stays LOW.
  // NOTE: calibration is latency-first, not answer-quality-verified — a small
  // synthetic low-vs-medium comparison passed both effort levels on bounded
  // checks with no demonstrated low-effort speed advantage in that sample
  // (see intent/2026-09-07-auto-effort/verification.md, issue #2893). Gate
  // any further loosening behind a proper answer-quality A/B.
  const q = question.toLowerCase()
  const langCode = locale.split("-")[0]
  const phraseSets = [
    ...(COMPLEXITY_PHRASES[langCode] ?? []),
    ...(langCode !== "en" ? (COMPLEXITY_PHRASES["en"] ?? []) : []),
  ]
  if (q.includes("```") || fileCount > 0 || phraseSets.some((phrase) => q.includes(phrase))) {
    return "medium"
  }
  return "low"
}
